"use client"

import { useMemo, useState } from "react"
import type { Team, User } from "@/schema"
import InputLabel from "@ui/badge-input/input-label"
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@ui/shad"
import { cn } from "@ui/utils"
import { Check, User as UserIcon, Users } from "lucide-react"

import { useFetchCustomFields } from "@/hooks/api/custom_fields"
import { useFetchTeams } from "@/hooks/api/teams"
import { useFetchUsers } from "@/hooks/api/users"

export interface TaskAssignmentSelectorProps {
  selectedAssignee: User | null
  setSelectedAssignee: (assignee: User | null) => void
  selectedTeam: Team | null
  setSelectedTeam: (team: Team | null) => void
  selectedUserReferenceFieldId?: string | null
  setSelectedUserReferenceFieldId?: (customFieldId: string | null) => void
  includeUserReferenceFields?: boolean
  className?: string
  bookId?: string
}

export default function TaskAssignmentSelector({
  selectedAssignee,
  setSelectedAssignee,
  selectedTeam,
  setSelectedTeam,
  selectedUserReferenceFieldId,
  setSelectedUserReferenceFieldId,
  includeUserReferenceFields = false,
  className,
  bookId,
}: TaskAssignmentSelectorProps) {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")

  const { data: usersData } = useFetchUsers(bookId)
  const { data: teamsData } = useFetchTeams({ bookIdOverride: bookId })
  const { data: customFieldsData } = useFetchCustomFields("Household", {
    enabled: includeUserReferenceFields,
  })

  const users = useMemo(() => usersData?.users ?? [], [usersData?.users])
  const teams = useMemo(() => teamsData?.teams ?? [], [teamsData?.teams])
  const userReferenceFields = useMemo(() => {
    if (!includeUserReferenceFields) return []
    return (customFieldsData?.custom_fields ?? []).filter((field) => field.field_type === "user_reference")
  }, [customFieldsData?.custom_fields, includeUserReferenceFields])

  const selectedUserReferenceField = useMemo(() => {
    if (!selectedUserReferenceFieldId) return null
    return userReferenceFields.find((field) => field.id === selectedUserReferenceFieldId) || null
  }, [selectedUserReferenceFieldId, userReferenceFields])

  // Filter locally based on search query
  const filteredUsers = useMemo(() => {
    const query = searchQuery.toLowerCase()
    const filtered = query
      ? users.filter((u) => u.full_name?.toLowerCase().includes(query) || u.email?.toLowerCase().includes(query))
      : users
    return [...filtered].sort((a, b) => (a.full_name ?? "").localeCompare(b.full_name ?? ""))
  }, [users, searchQuery])

  const filteredTeams = useMemo(() => {
    const query = searchQuery.toLowerCase()
    const filtered = query ? teams.filter((t) => t.name?.toLowerCase().includes(query)) : teams
    return [...filtered].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""))
  }, [teams, searchQuery])

  const filteredUserReferenceFields = useMemo(() => {
    if (!includeUserReferenceFields) return []
    const query = searchQuery.toLowerCase()
    const filtered = query
      ? userReferenceFields.filter(
          (field) => field.display_name.toLowerCase().includes(query) || field.field_key.toLowerCase().includes(query)
        )
      : userReferenceFields
    return [...filtered].sort((a, b) => a.display_name.localeCompare(b.display_name))
  }, [includeUserReferenceFields, searchQuery, userReferenceFields])

  const handleSelectUser = (user: User) => {
    if (selectedAssignee?.id === user.id) {
      setSelectedAssignee(null)
    } else {
      setSelectedAssignee(user)
      setSelectedTeam(null)
      setSelectedUserReferenceFieldId?.(null)
    }
    setOpen(false)
  }

  const handleSelectTeam = (team: Team) => {
    if (selectedTeam?.id === team.id) {
      setSelectedTeam(null)
    } else {
      setSelectedTeam(team)
      setSelectedAssignee(null)
      setSelectedUserReferenceFieldId?.(null)
    }
    setOpen(false)
  }

  const handleSelectUserReferenceField = (customFieldId: string) => {
    if (!setSelectedUserReferenceFieldId) return

    if (selectedUserReferenceFieldId === customFieldId) {
      setSelectedUserReferenceFieldId(null)
    } else {
      setSelectedUserReferenceFieldId(customFieldId)
      setSelectedAssignee(null)
      setSelectedTeam(null)
    }
    setOpen(false)
  }

  const hasSelection = selectedAssignee || selectedTeam || selectedUserReferenceField
  const hasResults = filteredUsers.length > 0 || filteredTeams.length > 0 || filteredUserReferenceFields.length > 0

  const getDisplayLabel = () => {
    if (selectedAssignee) return selectedAssignee.full_name
    if (selectedTeam) {
      return (
        <span className="inline-flex items-center gap-1">
          {selectedTeam.color && (
            <span className="size-2 flex-none rounded-full" style={{ backgroundColor: selectedTeam.color }} />
          )}
          {selectedTeam.name}
        </span>
      )
    }
    if (selectedUserReferenceField) return selectedUserReferenceField.display_name
    return "-"
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="lg"
          type="button"
          className={cn(
            "flex h-fit w-fit max-w-72 items-center justify-end gap-4 px-2",
            !hasSelection && "border-dashed",
            className
          )}
        >
          <InputLabel
            label={getDisplayLabel()}
            description="Assigned"
            className={cn(hasSelection ? "text-foreground" : "text-muted-foreground")}
          />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-64 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={includeUserReferenceFields ? "Search users, teams, or fields..." : "Search users or teams..."}
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            {searchQuery && !hasResults && (
              <CommandEmpty className="text-muted-foreground py-3 text-center text-sm">No results found</CommandEmpty>
            )}

            {filteredUsers.length > 0 && (
              <CommandGroup heading="Users">
                {filteredUsers.map((user) => (
                  <CommandItem
                    key={user.id}
                    value={`user-${user.id}`}
                    onSelect={() => handleSelectUser(user)}
                    className="flex items-center gap-2"
                  >
                    <UserIcon className="text-muted-foreground size-4" />
                    <span className="flex-1 truncate">{user.full_name}</span>
                    {selectedAssignee?.id === user.id && <Check className="text-foreground size-4" />}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {filteredTeams.length > 0 && (
              <CommandGroup heading="Teams">
                {filteredTeams.map((team) => (
                  <CommandItem
                    key={team.id}
                    value={`team-${team.id}`}
                    onSelect={() => handleSelectTeam(team)}
                    className="flex items-center gap-2"
                  >
                    <div className="flex items-center gap-1.5">
                      {team.color && <div className="size-2.5 rounded-full" style={{ backgroundColor: team.color }} />}
                      <Users className="text-muted-foreground size-4" />
                    </div>
                    <span className="flex-1 truncate">{team.name}</span>
                    {selectedTeam?.id === team.id && <Check className="text-foreground size-4" />}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {includeUserReferenceFields && filteredUserReferenceFields.length > 0 && (
              <CommandGroup heading="User reference fields">
                {filteredUserReferenceFields.map((customField) => (
                  <CommandItem
                    key={customField.id}
                    value={`custom-field-${customField.id}`}
                    onSelect={() => handleSelectUserReferenceField(customField.id)}
                    className="flex items-center gap-2"
                  >
                    <UserIcon className="text-muted-foreground size-4" />
                    <span className="flex-1 truncate">{customField.display_name}</span>
                    {selectedUserReferenceFieldId === customField.id && <Check className="text-foreground size-4" />}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
